import { useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useApi } from "../hooks/useApi";

import EditServiceDialog from "../dialogs/editServiceDialog";
import LoadingComponent from "../shared_modules/loadingComponent";

const BarberServicesDialog = ({
  showBarberServicesDialog,
  setShowBarberServicesDialog,
  setShowBarberServicesAlert,
  selectedBarber,
  onSave,
}) => {
  const api = useApi();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [barberName, setBarberName] = useState(null);
  const [services, setServices] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [showEditDialog, setShowEditDialog] = useState({
    show: false,
    service: null,
  });

  useEffect(() => {
    if (selectedBarber) {
      retrieveBarberServices();
    }
  }, [selectedBarber]);

  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      showBarberServicesDialog({ show: false, services: services });
    }
  };

  const retrieveBarberServices = async () => {
    setIsLoading(true);
    try {
      const response = await api(
        `/barbers/services.php?barber_id=${selectedBarber}`,
        { method: "GET" },
      );
      setBarberName(response.data.barber_name);
      setServices(response.data.services);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const saveBarberServices = async () => {
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });
    try {
      await api(`/barbers/services.php?barber_id=${selectedBarber}`, {
        method: "PUT",
        body: JSON.stringify({ services: services }),
      });
      setShowBarberServicesAlert({
        show: true,
        message: "Successfully updated services.",
        type: "success",
      });
      setShowBarberServicesDialog({ show: false, services: null });
      onSave();
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = (id) => {
    setServices(services.map((s) =>
      s.id === id ? { ...s, is_active: s.is_active === 1 ? 0 : 1 } : s,
    ));
  };

  const ServiceRow = ({ service, index, total }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        px: 3,
        py: 2,
        borderBottom: index !== total - 1 ? "0.1px solid" : "none",
        borderColor: "customDivider",
      }}
    >
      {/* Row 1: Name | duration · price */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {service.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {service.duration_minutes} mins
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>·</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ${service.price.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Row 2: Description | Edit + toggle */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12, flex: 1, maxWidth: "55%", mr: 1 }}>
          {service.description || ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => setShowEditDialog({ show: true, service })}
            sx={{ textTransform: "none", fontSize: 12, color: "text.secondary", minWidth: 0, px: 1 }}
          >
            Edit
          </Button>
          <Switch
            size="small"
            checked={!!service.is_active}
            onChange={() => toggleActive(service.id)}
            disabled={isSubmitting}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderServices = () => {
    const active = services.filter((s) => s.is_active === 1);
    const inactive = services.filter((s) => s.is_active !== 1);

    return (
      <Box>
        {active.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Active ({active.length})
            </Typography>
            <Box sx={{ borderRadius: 2, border: "0.1px solid", borderColor: "customDivider", backgroundColor: "offWhite", overflow: "hidden" }}>
              {active.map((s, i) => (
                <ServiceRow key={s.id} service={s} index={i} total={active.length} />
              ))}
            </Box>
          </Box>
        )}

        {inactive.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Inactive ({inactive.length})
            </Typography>
            <Box sx={{ borderRadius: 2, border: "0.1px solid", borderColor: "customDivider", backgroundColor: "offWhite", overflow: "hidden", opacity: 0.7 }}>
              {inactive.map((s, i) => (
                <ServiceRow key={s.id} service={s} index={i} total={inactive.length} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <EditServiceDialog
        showEditDialog={showEditDialog.show}
        setShowEditDialog={setShowEditDialog}
        selectedService={showEditDialog.service}
        onSave={retrieveBarberServices}
        showServiceAlert={showAlert}
        setServiceShowAlert={setShowAlert}
        type={"barbers"}
        selectedBarber={selectedBarber}
      />

      <Dialog
        open={showBarberServicesDialog}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {barberName} Services
          <IconButton
            aria-label="close"
            onClick={() => setShowBarberServicesDialog({ show: false, id: null })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {showAlert.show === true && (
            <Alert sx={{ mb: 2 }} severity={showAlert.type}>
              {showAlert.message}
            </Alert>
          )}

          {isLoading ? (
            <LoadingComponent marginTop={5} marginBottom={5} />
          ) : (
            services && (
              <Box sx={{ mt: 3 }}>
                {renderServices()}
              </Box>
            )
          )}
        </DialogContent>

        <DialogActions
          sx={{
            display: isLoading ? "none" : "flex",
            justifyContent: "flex-end",
            ml: 2,
            mr: 2,
          }}
        >
          <Box>
            <Button
              onClick={() => setShowBarberServicesDialog({ show: false, id: null })}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => saveBarberServices()}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress enableTrackSlot size="25px" sx={{ color: "text.primary" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BarberServicesDialog;
